/* eslint-disable react-hooks/exhaustive-deps */
import GoogleIcon from "@/assets/svgs/GoogleIcon";
import Loader from "@/components/atoms/Loader";
import { Modal } from "@/components/molecules/Modal";
import { OtpInputComponent } from "@/components/molecules/OtpInputComponent";
import ToastAlert from "@/components/molecules/ToastAlert";
import { Navbar } from "@/components/organisms/Navbar";
import SignupPhoneInput from "@/components/organisms/SignUpPhoneInput";
import SignupPageLayout from "@/components/templates/SignupPage/SignupPageLayout";
import {
  AUTH_EVENTS,
  LANDING_PAGE_EVENTS,
} from "@/constants/amplitude.constants";
import { GOOGLE_EVENTS } from "@/constants/googleTagEvents.constants";
import { GOOGLE_AUTH_URL } from "@/shared/constants/auth.constants";
import { CREATOR_GOOGLE_CLIENT_ID, GTM_ID, RECAPTCHA_TOKEN } from "@/shared/constants/env/externalService.constants";
import { sendGoogleTagEvent } from "@/shared/services/googleAnalytics.service";
import { ERRORS_COPIES } from "@/shared/constants/errors.constants";
import { SIGNIN_TYPE } from "@/shared/constants/signInConstants";
import { SIGNUP_TYPES } from "@/shared/constants/signUpConstants";
import SignupController from "@/shared/controllers/SignUp";
import { TOAST_TYPES } from "@/shared/data/enums/toast";
import { navbarData_en, navbarData_us } from "@/shared/data/navbar.data";
import { navbarData_kr } from "@/shared/data/navbar.data.kr";
import { signupRightPanelData_IN, signupRightPanelData_US } from "@/shared/data/signupRightPanel.data";
import Amplitude from "@/shared/services/amplitude.service";
import { useDataStore } from "@/shared/store/useDataStore";
import useModalStore, { AuthModalTypeEnum } from "@/shared/store/useModalStore";
import useSignUpStore from "@/shared/store/useSignUpStore";
import { SIGNUP_TYPE } from "@/shared/types/enums/signup";
import { SIGNUP_FORM_FIELDS } from "@/shared/types/signup.types";
import {
    getCurrentURL,
    getReferrer,
    getScreenWidth,
} from "@/shared/utils/commons";
import { isBrowser } from "@/shared/utils/dom";
import { emptyObject } from "@/shared/utils/empty";
import { isUceCountryCode } from "@/shared/utils/route.util";
import { onFormInputChange } from "@/shared/utils/signup";
import { spayeeAuthenticateSignUp } from "@/shared/utils/spayee.utils";
import { clearFromLocal } from "@/shared/utils/storage.util";
import { isRunningOnRootHost } from "@/shared/utils/url.util";
import { getUTMFieldsFromLocalStorage } from "@/shared/utils/utm.util";
import { Button, Div, INPUT_TYPES, Input } from "@graphy/ui";
import * as EmailValidator from "email-validator";
import generator from "generate-password";
import { GetServerSideProps, InferGetServerSidePropsType } from "next";
import dynamic from "next/dynamic";
import Head from "next/head";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";
import nookies from "nookies";
import { useCallback, useEffect, useRef, useState } from "react";
import BannerImgMweb from "../../assets/signup/signupBannerMobileNew.jpg";
import BannerImg from "../../assets/signup/signupBannerNew.jpg";
const SignInModal = dynamic(() => import("@/components/molecules/SignInModal"));

export const getServerSideProps: GetServerSideProps = async (context) => {
  const navbarData = {
    en: navbarData_en,
    us: navbarData_us,
    kr: navbarData_kr,
  }[(context.locale as "en" | "kr" | "us") ?? "en"];

  const cookies = nookies.get(context);
  const cookieCountryCode = cookies?.countryCode || "IN";
  const SIGNUP_VERSION_COOKIE = "signupVersion";
  let signupVersion = cookies?.[SIGNUP_VERSION_COOKIE] as "v1" | "v2" | undefined;

  if (signupVersion !== "v1" && signupVersion !== "v2") {
    signupVersion = Math.random() < 0.5 ? "v1" : "v2";
    nookies.set(context, SIGNUP_VERSION_COOKIE, signupVersion, {
      maxAge: 60 * 60 * 24 * 30,
      path: "/",
      sameSite: "lax",
    });
  }

  return {
    props: { navbarData, signupVersion, cookieCountryCode },
  };
};

const SignUp = ({
  navbarData,
  signupVersion: signupVersionProp,
  cookieCountryCode,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { openAuthModal, openOTPModal, setOpenOTPModal, isAuthModalOpen } = useModalStore();
  const [fpromTrack, setFpromTrack] = useState(null);
  const [fpromCode, setFpromCode] = useState(null);
  const [sentOTP, setSentOTP] = useState(false);
  const [domain, setDomain] = useState<string>("");
  const [resendOtp, setResendOtp] = useState(60);
  const router = useRouter();
  const { signupCopies } = navbarData;

  useEffect(() => {
    window.addEventListener("message", (event) => {
      if (
        event.origin.includes("graphy") ||
        event.origin === "http://localhost:3000"
      ) {
        setFpromTrack(event.data._fprom_track);
        setFpromCode(event.data._fprom_code);
      }
    });
  }, []);

  const { countryCode } = useDataStore();
  const finalCountryCode = countryCode || cookieCountryCode;
  const isUCECountry = isUceCountryCode(finalCountryCode);
  const signupVersion = isUCECountry ? "v2" : "v1";
  const path = usePathname();
  let intervalRef = useRef<ReturnType<typeof setInterval>>();

  const {
    signUpData,
    setSignUpData,
    contactNumber,
    signUpInputsErrors,
    setSignUpInputsErrors,
    callingCode,
    isFormLoading,
    setIsFormLoading,
    parentOrigin,
    OTPType,
    setOTPType,
  } = useSignUpStore();
  const isButtonDisabled =
    !signUpData?.schoolName ||
    !signUpData?.email ||
    !EmailValidator.validate(signUpData?.email) ||
    (!isUCECountry && !contactNumber);
  const onEmailSignUpInputChange = useCallback(
    (value: string, key: string) => {
      onFormInputChange(key);

      setSignUpData({
        ...signUpData,
        [key]: value,
      });
    },
    [setSignUpData, signUpData]
  );

  const clearFromLocalStorage = useCallback(() => {
    if (isRunningOnRootHost()) {
      clearFromLocal("utmFields");
      clearFromLocal("gref");
      clearFromLocal("QUERY_PARAMS");
    }
  }, []);

  const actionAmplitudeEvents = useCallback(
    (key: string) => {
      const {
        current_url,
        screen_width,
        referrer,
        utm_params,
        signup_type,
        email,
        signup_version,
      } = AUTH_EVENTS.SIGNUP_EMAIl_ENTERED.PROPERTIES;
      const { name } = AUTH_EVENTS.SIGNUP_NAME_ENTERED.PROPERTIES;
      if (key === SIGNUP_FORM_FIELDS.EMAIL) {
        new Amplitude().sendEvent({
          type: AUTH_EVENTS.SIGNUP_EMAIl_ENTERED.LABEL,
          properties: {
            [current_url]: getCurrentURL(),
            [screen_width]: getScreenWidth(),
            [referrer]: getReferrer(),
            [utm_params]: getUTMFieldsFromLocalStorage(),
            [signup_type]: SIGNUP_TYPES.FULLPAGE,
            [email]: signUpData?.email,
            [signup_version]: signupVersion,
          } as { [key: string]: string },
        });
      }

      if (key === SIGNUP_FORM_FIELDS.SCHOOL_NAME) {
        new Amplitude().sendEvent({
          type: AUTH_EVENTS.SIGNUP_NAME_ENTERED.LABEL,
          properties: {
            [current_url]: getCurrentURL(),
            [screen_width]: getScreenWidth(),
            [referrer]: getReferrer(),
            [utm_params]: getUTMFieldsFromLocalStorage(),
            [signup_type]: SIGNUP_TYPES.FULLPAGE,
            [name]: signUpData?.schoolName,
            [signup_version]: signupVersion,
          } as { [key: string]: string },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signUpData?.email, signUpData?.schoolName]
  );

  const onEmailSignUpInputBlur = useCallback(
    (value: string, key: string, shouldReturnMessage = false) => {
      if (!shouldReturnMessage) {
        actionAmplitudeEvents(key);
      }
      let errorMessage: string = "";

      const inputVal = (value || "").trim();

      if (inputVal !== "") {
        if (key === SIGNUP_FORM_FIELDS.EMAIL) {
          if (!EmailValidator.validate(inputVal)) {
            errorMessage = ERRORS_COPIES.INVALID_EMAIL;
          }
        }
      } else {
        if (key === SIGNUP_FORM_FIELDS.EMAIL) {
          errorMessage = ERRORS_COPIES.INVALID_EMAIL;
        } else if (key === SIGNUP_FORM_FIELDS.SCHOOL_NAME) {
          errorMessage = ERRORS_COPIES.REQUIRED_NAME;
        } else if (key === SIGNUP_FORM_FIELDS.PHONE) {
          errorMessage = ERRORS_COPIES.REQUIRED_PHONE;
        } else errorMessage = ERRORS_COPIES.REQUIRED_INPUT;
      }

      if (errorMessage && !shouldReturnMessage) {
        setSignUpInputsErrors({
          ...signUpInputsErrors,
          [key]: errorMessage,
        });
      }
      if (shouldReturnMessage) {
        return errorMessage;
      }
    },
    [actionAmplitudeEvents, setSignUpInputsErrors, signUpInputsErrors]
  );

  const actionSignUpSuccessAmplitudeEvent = useCallback(
    (userId: string) => {
      const {
        current_url,
        screen_width,
        referrer,
        utm_params,
        name,
        email,
        phone_number,
        phone_country,
        signup_type,
        signup_version
      } = AUTH_EVENTS.CREATOR_SIGNUP_SUCCESS.PROPERTIES;
      const amplitudeProperties = {
        [current_url]: getCurrentURL(),
        [screen_width]: getScreenWidth(),
        [referrer]: getReferrer(),
        [utm_params]: getUTMFieldsFromLocalStorage(),
        [name]: signUpData?.schoolName,
        [email]: signUpData?.email,
        [phone_number]: contactNumber,
        [phone_country]: callingCode,
        [signup_type]: SIGNUP_TYPES.FULLPAGE,
        [signup_version]: signupVersionProp,
      };
      new Amplitude().sendEvent({
        type: AUTH_EVENTS.CREATOR_SIGNUP_SUCCESS.LABEL,
        properties: amplitudeProperties as { [key: string]: string },
        userId: userId,
      });
    },
    [callingCode, contactNumber, signUpData?.email, signUpData?.schoolName, signupVersionProp]
  );

  const actionSignUpSuccessGTMEvent = useCallback(
    (userId: string) => {
      const {
        current_url,
        screen_width,
        referrer,
        utm_params,
        name,
        email,
        phone_number,
        phone_country,
        signup_type,
      } = AUTH_EVENTS.CREATOR_SIGNUP_SUCCESS.PROPERTIES;

      const GTMProperties = {
        [current_url]: getCurrentURL(),
        [screen_width]: getScreenWidth(),
        [referrer]: getReferrer(),
        [utm_params]: getUTMFieldsFromLocalStorage(),
        [name]: signUpData?.schoolName,
        [email]: signUpData?.email,
        [phone_number]: contactNumber,
        [phone_country]: callingCode,
        [signup_type]: SIGNUP_TYPES.FULLPAGE,
        userId,
      } as { [key: string]: string };

      sendGoogleTagEvent({
        eventName: GOOGLE_EVENTS.SIGNUP.INSTRUCTOR_SIGNUP_SUCCESS,
        gtmId: GTM_ID,
        isRootDataLayer: true,
        properties: GTMProperties,
      });
    },
    [callingCode, contactNumber, signUpData?.email, signUpData?.schoolName]
  );

  const actionSignUpSuccessServerAmplitudeEvent = useCallback(() => {
    const {
      referrer,
      utm_params,
      name,
      email,
      phone_number,
      phone_country,
      signup_type,
    } = AUTH_EVENTS.CRETOR_SIGNUP_SUCCESS_SERVER.PROPERTIES;
    const amplitudeProperties = {
      [referrer]: getReferrer(),
      [utm_params]: getUTMFieldsFromLocalStorage(),
      [name]: signUpData?.schoolName,
      [email]: signUpData?.email,
      [phone_number]: contactNumber,
      [phone_country]: callingCode,
      [signup_type]: SIGNUP_TYPES.FULLPAGE,
    };
    new Amplitude().sendEvent({
      type: AUTH_EVENTS.CRETOR_SIGNUP_SUCCESS_SERVER.LABEL,
      properties: amplitudeProperties as { [key: string]: string },
    });
  }, [callingCode, contactNumber, signUpData?.email, signUpData?.schoolName]);

  const handleOTPSignup = useCallback(
    async (otpType: string) => {
      let token = "";

      const password = generator.generate({
        length: 10,
        numbers: true,
      });
      //@ts-ignore
      if (typeof grecaptcha !== "undefined") {
        //@ts-ignore
        token = await grecaptcha?.execute(RECAPTCHA_TOKEN, {
          action: "submit",
        });
      }

      // console.log(OTPType?.otpType, "otpType>>", resendOtp);

      const { email, schoolName } = signUpData || emptyObject;
      const phoneValid = !!contactNumber;
      if (EmailValidator.validate(email) && phoneValid && !!schoolName) {
        const OTPVerificationValues = {
          email,
          phone: encodeURIComponent(`+${callingCode}${contactNumber}`),
          type: otpType,
          token,
          name: schoolName,
          ...(isUCECountry && { source: "graphy-us" }),
        };

        const otpVerified = await SignupController.verifyOTP(
          OTPVerificationValues
        );

        if (otpVerified.response) {
          setOpenOTPModal(true);
          setOTPType(otpType);
          setSentOTP(true);
          // setResendOtp(60);
          handleOTPResend();
        }

        if (!otpVerified.response && otpVerified?.message) {
          ToastAlert({
            toastType: TOAST_TYPES.ERROR,
            message: otpVerified?.message,
          });
        }

        // if(otpVerified.message){

        // }

        // happy case
        // if (emailResponse.response && !emailResponse.old_graphy_user) {
        //   const { domain } = emailResponse;
        //   //@ts-ignore
        //   actionSignUpSuccessAmplitudeEvent(emailResponse.userId);
        //   // spayeeAuthenticateSignUp({
        //   //   domain,
        //   //   email,
        //   //   password,
        //   // });
        // }
        // if (!emailResponse.response && emailResponse.message) {
        //   ToastAlert({
        //     toastType: TOAST_TYPES.ERROR,
        //     message: emailResponse?.message,
        //   });
        // }
        // if (emailResponse.old_graphy_user) {
        //   if (parentOrigin) {
        //     window.location.href = `${parentOrigin}/?signin&email=${email}`;
        //   } else {
        //     window.location.href = `/?signin&email=${email}`;
        //   }
        // }
      } else {
        let inputErrors = {
          email:
            onEmailSignUpInputBlur(email, SIGNUP_FORM_FIELDS.EMAIL, true) || "",
          phone: isUCECountry
            ? ""
            : onEmailSignUpInputBlur(
                contactNumber,
                SIGNUP_FORM_FIELDS.PHONE,
                true
              ) || "",
          schoolName:
            onEmailSignUpInputBlur(
              schoolName,
              SIGNUP_FORM_FIELDS.SCHOOL_NAME,
              true
            ) || "",
        };

        // setIsFormLoading(false);
        setSignUpInputsErrors(inputErrors);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      callingCode,
      contactNumber,
      onEmailSignUpInputBlur,
      parentOrigin,
      setSignUpInputsErrors,
      signUpData,
    ]
  );

  const decreaseTimer = () => {
    setResendOtp(function (prevCount) {
      if (prevCount > 0) {
        return (prevCount -= 1);
      } else {
        return (prevCount = 0);
      }
    });
  };

  const handleOTPResend = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(decreaseTimer, 1000);
  };

  const actionResendOTPOnSignUp = () => {
    new Amplitude().sendEvent({
    type: 'Resend_otp_Clicked',
    properties: {
      [AUTH_EVENTS.LOGIN_MODAL_RESEND_OTP_TAPPED.PROPERTIES.current_url]: getCurrentURL(),
      [AUTH_EVENTS.LOGIN_MODAL_RESEND_OTP_TAPPED.PROPERTIES.screen_width]: getScreenWidth(),
      [AUTH_EVENTS.LOGIN_MODAL_RESEND_OTP_TAPPED.PROPERTIES.referrer]: getReferrer(),
      [AUTH_EVENTS.LOGIN_MODAL_RESEND_OTP_TAPPED.PROPERTIES.utm_params]: getUTMFieldsFromLocalStorage(),
      signupVersion: signupVersion,
    } as { [key: string]: string },
  });
    setResendOtp(60);
    handleOTPResend();
    handleOTPSignup(OTPType);
  };

  const closeModal = () => {
    setOpenOTPModal(false);
    setSentOTP(false);
    setOTPType("");
    setResendOtp(60);
    // const { current_url, screen_width, referrer, utm_params } =
    //   AUTH_EVENTS.LOGIN_MODAL_DISMISSED.PROPERTIES;

    // if (authModalType === AuthModalTypeEnum.SignIn) {
    //   new Amplitude().sendEvent({
    //     type: AUTH_EVENTS.LOGIN_MODAL_DISMISSED.LABEL,
    //     properties: {
    //       [current_url]: getCurrentURL(),
    //       [screen_width]: getScreenWidth(),
    //       [referrer]: getReferrer(),
    //       [utm_params]: getUTMFieldsFromLocalStorage(),
    //     } as { [key: string]: string },
    //   });
    // } else {
    //   setOpenSignUp(false);
    //   setSentOTP(false);
    //   setContactNumber("");
    //   setSignUpData({ ...signUpData, email: "" });
    //   setOTPType("");
    //   new Amplitude().sendEvent({
    //     type: AUTH_EVENTS.SIGNUP_MODAL_DISMISSED.LABEL,
    //     properties: {
    //       [current_url]: getCurrentURL(),
    //       [screen_width]: getScreenWidth(),
    //       [referrer]: getReferrer(),
    //       [utm_params]: getUTMFieldsFromLocalStorage(),
    //     } as { [key: string]: string },
    //   });
    // }
    // closeAuthModal();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const renderOTPOnSignUp = useCallback(() => {
    return (
      <Modal showModal={openOTPModal} onClose={closeModal}>
        <div className="flex flex-col gap-14">
          <div className="flex flex-col gap-2">
            <h2 className="font-inter text-[22px] font-semibold leading-[24px] tracking-[-0.0125em] text-[#393F41]">
              Enter code to verify
            </h2>
            <p className="font-inter text-[14px] font-normal leading-[20px] tracking-[-0.005em] text-[#878C90]">
              {`We've sent a 5 digit code to ${OTPType === "phone" ? `+${callingCode}${contactNumber}` : signUpData?.email}`}
            </p>
            {!sentOTP && (
              <span className="text-[14px] leading-[20px] text-[#878C90]">Sending...</span>
            )}
          </div>
          {sentOTP && (
            <OtpInputComponent
              creatorDomain={domain}
              signupCopies={signupCopies}
              OTPActionType={"signup"}
              resendOtp={resendOtp}
              onResend={actionResendOTPOnSignUp}
            />
          )}
        </div>
        <form
          method="post"
          target="_parent"
          id="spayee-signup-form"
          className="hidden"
        >
          <input id="spayee-signup-form-email" name="email" />
          <input id="spayee-signup-form-password" name="password" />
          <input id="spayee-signup-form-age" name="age" value="" readOnly />
        </form>
      </Modal>
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [OTPType, sentOTP, resendOtp]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleEmailSignup = useCallback(async () => {
    let token = "";

    const password = generator.generate({
      length: 10,
      numbers: true,
    });
    //@ts-ignore
    if (typeof grecaptcha !== "undefined") {
  //@ts-ignore
      token = await grecaptcha?.execute(RECAPTCHA_TOKEN, {
        action: "submit",
      });
    }

    const { email, schoolName } = signUpData || emptyObject;
    const phoneValid = !!contactNumber;
    if (EmailValidator.validate(email) && phoneValid && !!schoolName) {
      const signupValues = {
        email,
        phone: `+${callingCode}${contactNumber}`,
        countryCode: callingCode,
        name: schoolName,
        recaptchaToken: token,
        signupType: SIGNUP_TYPE.NORMAL,
        password: password,
        _fprom_code: fpromCode,
        _fprom_track: fpromTrack,
      };

      // @ts-ignore
      const emailResponse = await SignupController.emailSignup(signupValues);

      if (emailResponse.response && !emailResponse.old_graphy_user) {
        clearFromLocalStorage();
        const { domain } = emailResponse;
        //@ts-ignore
        actionSignUpSuccessAmplitudeEvent(emailResponse.userId);
        if (emailResponse?.userId) {
          actionSignUpSuccessGTMEvent(emailResponse.userId);
        }
        spayeeAuthenticateSignUp({
          domain,
          email,
          password,
        });
      }
      if (emailResponse.response && emailResponse.domain) {
        actionSignUpSuccessServerAmplitudeEvent();
      }
      if (!emailResponse.response && emailResponse.message) {
        ToastAlert({
          toastType: TOAST_TYPES.ERROR,
          message: emailResponse?.message,
        });
      }
      if (emailResponse.old_graphy_user) {
        if (parentOrigin) {
          window.location.href = `${parentOrigin}/?signin&email=${email}`;
        } else {
          window.location.href = `/?signin&email=${email}`;
        }
      }
    } else {
      let inputErrors = {
        email:
          onEmailSignUpInputBlur(email, SIGNUP_FORM_FIELDS.EMAIL, true) || "",
        phone: isUCECountry
          ? ""
          : onEmailSignUpInputBlur(
              contactNumber,
              SIGNUP_FORM_FIELDS.PHONE,
              true
            ) || "",
        schoolName:
          onEmailSignUpInputBlur(
            schoolName,
            SIGNUP_FORM_FIELDS.SCHOOL_NAME,
            true
          ) || "",
      };

      setSignUpInputsErrors(inputErrors);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    actionSignUpSuccessAmplitudeEvent,
    actionSignUpSuccessServerAmplitudeEvent,
    actionSignUpSuccessGTMEvent,
    callingCode,
    contactNumber,
    fpromCode,
    fpromTrack,
    onEmailSignUpInputBlur,
    parentOrigin,
    setSignUpInputsErrors,
    signUpData,
  ]);

  const handleOTPVerification = () => {
    let otpType = "";
    if (countryCode === "IN" && callingCode === "91") {
      otpType = "phone";
    } else {
      otpType = "email";
    }
    const {
      current_url,
      screen_width,
      referrer,
      utm_params,
      signup_type,
      signup_version,
      otp_type,
    } = AUTH_EVENTS.SIGNUP_GET_STARTED_CLICKED.PROPERTIES;

    new Amplitude().sendEvent({
      type: AUTH_EVENTS.SIGNUP_GET_STARTED_CLICKED.LABEL,
      properties: {
        [current_url]: getCurrentURL(),
        [screen_width]: getScreenWidth(),
        [referrer]: getReferrer(),
        [utm_params]: getUTMFieldsFromLocalStorage(),
        [signup_type]: SIGNUP_TYPES.FULLPAGE,
        [signup_version]: signupVersion,
        [otp_type]: otpType,
      } as { [key: string]: string },
    });

    handleOTPSignup(otpType);
  };

  const actionOpenSignIn = () => {
    const { current_url, screen_width, referrer, source, utm_params, index, signup_version } =
      LANDING_PAGE_EVENTS.SIGN_IN_TAPPED.PROPERTIES;

    new Amplitude().sendEvent({
      type: LANDING_PAGE_EVENTS.SIGN_IN_TAPPED.LABEL,
      properties: {
        [current_url]: getCurrentURL(),
        [screen_width]: getScreenWidth(),
        [referrer]: getReferrer(),
        [source]: "signup_page",
        [utm_params]: getUTMFieldsFromLocalStorage(),
        [index]: 0,
        [signup_version]: signupVersion,
      } as { [key: string]: string },
    });

    openAuthModal(AuthModalTypeEnum.SignIn, true);
  };

  const googleLogin = () => {
    const { current_url, utm_params, signup_version, screen_width, referrer, method, signup_type } =
      AUTH_EVENTS.SIGNUP_GOOGLE_CLICKED.PROPERTIES;
    new Amplitude().sendEvent({
      type: AUTH_EVENTS.SIGNUP_GOOGLE_CLICKED.LABEL,
      properties: {
        [current_url]: getCurrentURL(),
        [screen_width]: getScreenWidth(),
        [referrer]: getReferrer(),
        [utm_params]: getUTMFieldsFromLocalStorage(),
        [signup_version]: signupVersion,
        [method]: SIGNIN_TYPE.GOOGLE,
        [signup_type]: SIGNUP_TYPES.FULLPAGE,
      } as { [key: string]: string },
    });
    const googleLink = `${GOOGLE_AUTH_URL}?redirect_uri=${window.location.origin}/google/success&response_type=code id_token&scope=email profile openid&openid.realm&include_granted_scopes=true&client_id=${CREATOR_GOOGLE_CLIENT_ID}&ss_domain=${window.location.origin}&fetch_basic_profile=true&gsiwebsdk=2&flowName=GeneralOAuthFlow&prompt=select_account&state=login&nonce=${Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)}`;
    window.location.href = googleLink;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const renderEmailSignUpForm = useCallback(() => {
    return (
      <div className="flex flex-col gap-[24px]">
        <div className="flex flex-col gap-[8px]">
          <h3 className="w-full text-center md:text-left font-semibold text-[16px] md:text-[22px] leading-[26.56px] md:leading-[24px] tracking-[-0.0125em] text-[#393F41] m-0">
            {signupVersion === "v2" ? signupCopies.SignupHeadingAiv2 : signupCopies.SignupHeadingAi}
          </h3>
          <p className="text-[14px] leading-[20px] font-normal tracking-[-0.005em] text-[#393F41] md:text-[#878C90] text-center md:text-left font-inter">
            {signupVersion === "v2" ? signupCopies.signupDescriptionv2 : signupCopies.signupDescription}
          </p>
        </div>
        <div className="flex flex-col gap-[20px]">
          {/* Google signup temporarily disabled
          {isUceCountryCode(finalCountryCode) && (
            <>
              <Button
                className="flex h-[56px] w-full items-center justify-center gap-[8px] rounded-[12px] border border-[#E5E5E5] bg-white"
                onClick={googleLogin}
                ripple={false}
              >
                <GoogleIcon />
                <p className="font-inter text-[16px] font-[500] leading-[24px] text-[#393F41]">
                  {signupCopies.ctas.CONTINUE_WITH_GOOGLE}
                </p>
              </Button>
              <div className="flex items-center gap-[12px]">
                <div className="h-[1px] flex-1 border-t border-[#E5E5E5]" />
                <span className="font-inter text-[14px] font-[400] leading-[24px] text-[#AAB1B7]">or</span>
                <div className="h-[1px] flex-1 border-t border-[#E5E5E5]" />
              </div>
            </>
          )}
          */}
          <Input
            containerMargin=""
            className="h-[56px] w-full rounded-[12px] border border-[#E5E5E5] px-[24px] py-[14px]"
            type={INPUT_TYPES.TEXT}
            value={signUpData?.schoolName}
            inputLimit={200}
            errorMessage={signUpInputsErrors?.[SIGNUP_FORM_FIELDS.SCHOOL_NAME]}
            onChange={(value) => {
              onEmailSignUpInputChange(value as string, SIGNUP_FORM_FIELDS.SCHOOL_NAME);
            }}
            onBlur={(value) => {
              onEmailSignUpInputBlur(value, SIGNUP_FORM_FIELDS.SCHOOL_NAME);
            }}
            placeholder={signupCopies.name.placeholder}
          />
          <Input
            containerMargin=""
            className="h-[56px] w-full rounded-[12px] border border-[#E5E5E5] px-[24px] py-[14px]"
            type={INPUT_TYPES.EMAIL}
            inputLimit={200}
            errorMessage={signUpInputsErrors?.[SIGNUP_FORM_FIELDS.EMAIL]}
            onChange={(value) =>
              onEmailSignUpInputChange(value as string, SIGNUP_FORM_FIELDS.EMAIL)
            }
            value={signUpData?.email}
            onBlur={(value) =>
              onEmailSignUpInputBlur(value, SIGNUP_FORM_FIELDS.EMAIL)
            }
            placeholder={signupCopies.email.placeholder}
          />
          <SignupPhoneInput signupCopies={signupCopies} />
        </div>
        <div className="relative w-full">
          <div className="relative w-full">
            <Button
              disabled={isFormLoading}
              onClick={handleOTPVerification}
              ripple={false}
              clickId="signup-button"
              className={`
      flex w-full items-center justify-center
      h-[44px] md:h-[48px]
      px-[20px] md:px-[24px]
      py-[12px]
      gap-[9px] md:gap-[8px]
      rounded-[111px] md:rounded-[100px]
      bg-[#4E5DE0]
      transition-all duration-200
      ${isFormLoading
                  ? "cursor-not-allowed"
                  : "hover:shadow-[0px_0px_16px_rgba(83,97,233,0.5)] cursor-pointer"
                }
    `}
            >
              <Div className="
      font-inter font-medium
      text-[16px]
      leading-[20px]
      tracking-[-0.005em]
      text-center
      text-[#F7F7F7]
    ">
                {isFormLoading ? <Loader /> : signupCopies.ctas.GET_STARTED_FOR_FREE}
              </Div>
            </Button>
          </div>
        </div>
        <div className="flex flex-col items-center gap-[20px]">
          <div className="flex flex-wrap items-center justify-center gap-x-1 text-center">
            <span className="font-inter text-[14px] font-normal leading-[22.13px] md:leading-[20px] tracking-[0.015em] text-[#878C90] md:text-[#AAB1B7]">
              {signupCopies.alreadyHaveAccount}
            </span>

            <span
              className="cursor-pointer font-inter text-[14px] leading-[22.13px] md:leading-[20px] tracking-[0em] md:tracking-[0.015em] text-purple-3 font-medium md:font-normal"
              onClick={actionOpenSignIn}
            >
              {signupCopies.ctas.LOGIN}
            </span>
          </div>
          <span className="block w-full text-center px-2 md:px-0 font-inter text-[14px] leading-[22.13px] md:leading-[20px] tracking-[0.005em] text-[#878C90] md:text-[#AAB1B7] font-normal md:font-medium">
            By continuing, you agree to our{" "}
            <a
              href="https://graphy.com/legal/privacy/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Terms & Services
            </a>
          </span>
        </div >
        <form
          method="post"
          target="_parent"
          id="spayee-signup-form"
          className="hidden"
        >
          <input id="spayee-signup-form-email" name="email" />
          <input id="spayee-signup-form-password" name="password" />
          <input id="spayee-signup-form-age" name="age" value="" readOnly />
        </form>
      </div>
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    signUpData?.schoolName,
    signUpData?.email,
    signUpInputsErrors,
    handleOTPSignup,
    isFormLoading,
    onEmailSignUpInputChange,
    onEmailSignUpInputBlur,
    OTPType,
    resendOtp,
  ]);

  useEffect(() => {
    const {
      current_url,
      screen_width,
      referrer,
      variation,
      utm_params,
      visitor_id,
    } = AUTH_EVENTS.SIGNUP_PAGE_VIEWED.PROPERTIES;

    new Amplitude().sendEvent({
      type: AUTH_EVENTS.SIGNUP_PAGE_VIEWED.LABEL,
      properties: {
        [current_url]: getCurrentURL(),
        [screen_width]: getScreenWidth(),
        [referrer]: getReferrer(),
        [utm_params]: getUTMFieldsFromLocalStorage(),
        [variation]: signupVersion,
        [visitor_id]: localStorage.getItem('graphy_visitor_id') ?? "",
      } as { [key: string]: string },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signupVersion]);

  const rightPanelData = isUCECountry ? signupRightPanelData_US : signupRightPanelData_IN;
  return (
    <>
      <Head>
        <title>Graphy - Signup</title>
      </Head>
      {signupVersion === "v1" ? (
        <div className="mx-auto flex flex-col md:flex-row md:items-start md:py-0 md:gap-[40px] tab:flex-row tab:gap-[95px] tab:py-0 web:flex-row web:gap-[174px] md:pr-[50px] pl-0 md:pl-[16px] lg:pl-[233px]">
          <div className="w-full ">
            <Navbar navbarData={{ navbarItems: [], signupCopies: navbarData.signupCopies }} />
            <div className="block md:hidden w-full pt-2 pl-[16px] pr-[16px]">
              <Image
                src={BannerImgMweb}
                className="w-[348px] h-[200px] mx-auto object-cover rounded-[12px] border-[0.38px] border-[#E5E7EB]"
                alt="signup-heroImage-mobile"
                priority
                quality={100}
              />
            </div>
            <div className="w-full pt-0 md:pt-[50px] lg:pt-[100px] md:w-[290px] lg:w-[400px] pl-[15px] pr-[15px] md:pl-0 md:pr-0">
              {renderEmailSignUpForm()}
            </div>
          </div>
          <div className="hidden md:block w-full md:w-[1900px] md:h-[670px] lg:h-[700px] mt-[24px]">
            <Image
              src={BannerImg}
              className="w-full h-full object-cover rounded-[24px] overflow-y-hidden"
              alt="signup-heroImage"
              priority
              quality={100}
            />
          </div>
        </div>
      ) : (
        <SignupPageLayout
          navbarData={navbarData}
          leftContent={renderEmailSignUpForm()}
          rightPanelData={rightPanelData}
          version="v2"
          isUCECountry={isUCECountry}
        />
      )}

      {openOTPModal && renderOTPOnSignUp()}
      {isAuthModalOpen && <SignInModal signupCopies={signupCopies} />}
    </>
  );
};
export default SignUp;